// Tracks whether the current form has unsaved changes, so navigating away
// (sidebar, Back buttons, closing/reloading the tab) can warn first.
import { useCallback, useEffect, useRef } from "react";

const MESSAGE = "You have unsaved changes. Do you want to leave without saving?";
let hasUnsavedChanges = false;

// Returns true when it's OK to navigate away: nothing unsaved, or the user chose to discard.
// ponytail: native confirm dialog, swap for an MUI dialog if design asks for a styled one
export const confirmDiscardChanges = () => {
  if (!hasUnsavedChanges) return true;
  if (!window.confirm(MESSAGE)) return false;
  hasUnsavedChanges = false;
  return true;
};

// Call after a successful save, before navigating away.
export const clearUnsavedChanges = () => {
  hasUnsavedChanges = false;
};

// Returns trackChanges(value): the first value seen is the baseline, any later
// value that differs from it marks the form dirty. Also warns on tab close/reload.
export const useUnsavedChanges = () => {
  const baseline = useRef(null);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      hasUnsavedChanges = false;
    };
  }, []);

  return useCallback((value) => {
    const snapshot = JSON.stringify(value);
    if (baseline.current === null) {
      baseline.current = snapshot;
      return;
    }
    hasUnsavedChanges = snapshot !== baseline.current;
  }, []);
};
